import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Twitter, Anchor } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      const { data } = await api.post("/newsletter", { email });
      toast.success(data.message || "Subscribed.");
      setEmail("");
    } catch (err) {
      toast.error("Couldn't subscribe — try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer data-testid="site-footer" className="bg-[#2D3032] text-[#F4F1EA] mt-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-16 grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="flex items-center gap-2 mb-4">
            <span className="w-8 h-8 bg-[#EBAA86] text-[#2D3032] flex items-center justify-center">
              <Anchor size={16} strokeWidth={1.8} />
            </span>
            <span className="font-heading text-xl">Gulf Coast Home Co.</span>
          </div>
          <p className="text-sm text-[#A8ADB0] max-w-sm leading-relaxed">
            Built to feel lived in. Rugged Gulf Coast finds for backwater living, dock life, outdoor gear, and everyday carry.
          </p>
        </div>

        <div className="md:col-span-3">
          <p className="text-xs uppercase tracking-[0.24em] text-[#8EAD9E] mb-4">Explore</p>
          <ul className="space-y-2 text-sm">
            <li><Link to="/" data-testid="footer-link-shop" className="hover:text-[#EBAA86]">Shop</Link></li>
            <li><Link to="/blog" data-testid="footer-link-journal" className="hover:text-[#EBAA86]">Journal</Link></li>
            <li><Link to="/about" data-testid="footer-link-about" className="hover:text-[#EBAA86]">About</Link></li>
            <li><Link to="/contact" data-testid="footer-link-contact" className="hover:text-[#EBAA86]">Contact</Link></li>
          </ul>
        </div>

        <div className="md:col-span-4">
          <p className="text-xs uppercase tracking-[0.24em] text-[#8EAD9E] mb-4">Letters from the coast</p>
          <form onSubmit={subscribe} data-testid="newsletter-form" className="flex">
            <input
              type="email"
              required
              data-testid="newsletter-email-input"
              placeholder="you@beach.house"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="flex-1 bg-transparent border-b border-[#636769] focus:border-[#EBAA86] outline-none py-2 text-sm placeholder:text-[#636769]"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="newsletter-submit-btn"
              className="ml-3 text-xs uppercase tracking-[0.2em] text-[#EBAA86] hover:text-white disabled:opacity-50"
            >
              {loading ? "..." : "Subscribe"}
            </button>
          </form>
          <div className="flex gap-4 mt-6">
            <a href="https://pinterest.com/gulfcoasthomeco" target="_blank" rel="noopener noreferrer" data-testid="social-instagram" aria-label="Instagram" className="text-[#A8ADB0] hover:text-[#EBAA86]"><Instagram size={18} /></a>
            <a href="https://pinterest.com/gulfcoasthomeco" target="_blank" rel="noopener noreferrer" data-testid="social-twitter" aria-label="Twitter" className="text-[#A8ADB0] hover:text-[#EBAA86]"><Twitter size={18} /></a>
            <a href="https://pinterest.com/gulfcoasthomeco" target="_blank" rel="noopener noreferrer" data-testid="social-pinterest" aria-label="Pinterest" className="text-[#A8ADB0] hover:text-[#EBAA86]">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.402.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.357-.629-2.748-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12.017 24c6.624 0 11.99-5.367 11.99-11.987C24.007 5.367 18.641.001 12.017.001z"/></svg>
            </a>
          </div>
        </div>
      </div>
      <div className="border-t border-[#3D4042]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-6 flex flex-col md:flex-row justify-between gap-3 text-xs text-[#A8ADB0]">
          <span>© {new Date().getFullYear()} Gulf Coast Home Co. — Practical Coastal Living.</span>
          <span data-testid="affiliate-disclosure" className="md:text-right max-w-md">
            As an affiliate, we may earn from qualifying purchases.
          </span>
        </div>
      </div>
    </footer>
  );
}
